import { GeneralDimensionsConfig } from '../../dimensionsUtils';

const convertToAutocompleteOption = value => ({
    label: value,
    value,
});

export const getAutocompleteOptions = options =>
    Array.from(new Set(options.flatMap(({ tags }) => tags))).map(convertToAutocompleteOption);

export const isEqualCreatives = (creatives, comparedCreatives) => {
    if (creatives.length !== comparedCreatives.length) return false;

    const creativesHashes = new Set(creatives.map(({ imageHash }) => imageHash));
    const comparedCreativesHashes = new Set(comparedCreatives.map(({ imageHash }) => imageHash));

    for (const hash of creativesHashes) {
        if (!comparedCreativesHashes.has(hash)) return false;
    }

    return true;
};

export const isGeneralDimension = dimensionName => GeneralDimensionsConfig[dimensionName];

export const filterCreativesByGeneralDimensions = (creatives, selectedDimensions, dimensionToSelectedTags) => {
    const valuesByFilters = Object.keys(GeneralDimensionsConfig).reduce((acc, filter) => {
        acc[filter] = dimensionToSelectedTags[filter] ?? (selectedDimensions.includes(filter) ? [] : null);
        return acc;
    }, {});

    return creatives
        .map(creative => {
            if (creative.hide) return null;

            let updatedCreative = creative;
            Object.entries(GeneralDimensionsConfig).some(([filterName, { filterBy }]) => {
                updatedCreative = filterBy(updatedCreative, valuesByFilters[filterName]);
                return !updatedCreative;
            });
            return updatedCreative;
        })
        .filter(Boolean);
};
export const filterCreativesByAIDimensions = (
    creatives,
    aiSelectedDimensions,
    dimensionToSelectedTags,
    dimensionToCreativeTags
) => {
    const aiDimensionsWithTags = aiSelectedDimensions.filter(dimension => !!dimensionToCreativeTags[dimension]);

    if (!aiDimensionsWithTags.length) return creatives;

    const imageHashToFilteredTags = aiDimensionsWithTags.reduce((acc, dimensionName) => {
        const dimensionTags = dimensionToSelectedTags[dimensionName] || [];
        const creativesTagsData = dimensionToCreativeTags[dimensionName] || { options: [] };

        creativesTagsData.options.forEach(({ imageHash, category, tags: creativeTags }) => {
            if (!acc[imageHash]) acc[imageHash] = {};

            if (!dimensionTags.length) {
                acc[imageHash][dimensionName] = { category, tags: creativeTags };
                return;
            }

            const filteredTagExists = !!creativeTags.find(tag => dimensionTags.includes(tag));

            const sortedTags = filteredTagExists
                ? [
                      ...creativeTags.filter(tag => dimensionTags.includes(tag)),
                      ...creativeTags.filter(tag => !dimensionTags.includes(tag)),
                  ]
                : [];

            acc[imageHash][dimensionName] = { category, tags: sortedTags };
        });

        return acc;
    }, {});

    const aiDimensionsWithSelectedTags = aiSelectedDimensions.filter(dimension => !!dimensionToSelectedTags[dimension]);

    return creatives
        .map(creative => {
            const aiTags = imageHashToFilteredTags[creative.imageHash];

            const aiTagsList = Object.entries(aiTags || {}).map(([dimensionName, data]) => ({
                dimensionName,
                ...data,
            }));

            // In case there are not tags filtered.
            if (!aiDimensionsWithSelectedTags.length) return { ...creative, aiTags: aiTagsList };

            // Filter out creatives with no AI tags when dimensionToSelectedTags is not empty == AI tag is filtered.
            if (!aiTags) return null;

            const shouldExcludeCreative = aiDimensionsWithSelectedTags.some(dimensionName => {
                const selectedTags = dimensionToSelectedTags[dimensionName] || [];
                return selectedTags.length && !aiTags[dimensionName]?.tags.length;
            });

            return shouldExcludeCreative ? null : { ...creative, aiTags: aiTagsList };
        })
        .filter(Boolean);
};

export const isAsyncFilter = filterName => GeneralDimensionsConfig[filterName]?.isMultiValued;

export const getMultiValuedDimensionTags = (creatives, dimension) =>
    getAutocompleteOptions(
        creatives.map(({ multiValuedDimensions }) => ({ tags: multiValuedDimensions[dimension] }))
    ).filter(({ value }) => value);

export const getSingleValuedDimensionTags = (creatives, dimension) =>
    getAutocompleteOptions(creatives.map(creative => ({ tags: [creative[dimension]] })));
